'use client'

import { reportHandledException } from '@/lib/error-reporting'
import { toastTitle } from '@/lib/toast-title'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { isTeamMemberDisabledError } from '@kiao-voice/api-client'
import { useTranslation } from 'react-i18next'
import { Pencil } from 'lucide-react'
import { SegmentedControl, toast } from '@kiao-voice/ui-kit'
import { TRANSCRIPT_FORMAT_MIME, type UserUseCase } from '@kiao-voice/core'
import { WorkspacePageHeader } from '@/components/workspace-indicator'
import { useWorkspaceScopedLoader } from '@kiao-voice/hooks'
import { useInlineEditable } from '@/components/use-inline-editable'
import { downloadTextFile } from '@/lib/download-blob'
import { useAuthModal } from '@/features/auth/auth-modal-provider'
import { useActiveContext } from '@/lib/use-active-context'
import { clearStoredToken, onAuthTokenChange, readStoredToken } from '@/features/auth/auth-storage'
import {
  AudioBatchApiError,
  fetchAudioBatchStatus,
  fetchAudioItemTranscript,
  isRecoverableActiveContextError,
  listAudioBatches,
  probeInternalAccess,
  renameAudioItem,
  retryAudioItem,
  submitAudioBatch,
  unshareAudioItemFromTeam,
} from '@/features/batch-transcription/api-client'
import {
  AUDIO_BATCH_DOWNLOAD_FORMATS,
  type AudioBatchDownloadFormat,
} from '@/features/batch-transcription/download-formats'
import {
  RowProgressRail,
  RowStagePercent,
  RowStageText,
} from '@/features/batch-transcription/row-progress'
import {
  failedStageView,
  pendingStageView,
  type ItemStageView,
} from '@/features/batch-transcription/stage-progress'
import {
  progressRowKey,
  useBatchProgressPolling,
} from '@/features/batch-transcription/use-batch-progress-polling'
import {
  RETENTION_SOON_DAYS,
  RETENTION_WARN_DAYS,
  daysUntilExpiry,
  deriveTranscriptFilename,
  formatDate,
  formatTimecode,
  stripAudioExtension,
} from '@/features/batch-transcription/transcript-view-model'
import {
  type AudioBatchDisplayStatus,
  type AudioBatchSubmitResponse,
  type AudioTranscriptListItem,
  type AudioTranscriptListResponse,
} from '@/features/batch-transcription/types'
import { UploadModal } from './upload-modal'
import { UseCaseQuestion } from '@/features/use-case-question/use-case-question'
import {
  fetchUseCaseQuestionOpen,
  saveUseCase,
} from '@/features/use-case-question/api-client'
import { ANALYTICS_EVENTS, trackEvent } from '@/lib/analytics'
import { OwnedPublicNoteSharesDialog } from '@/features/meeting-notes/owned-public-note-shares-dialog'
import {
  listOwnedPublicNoteShares,
  revokeOwnedPublicNoteShare,
} from '@/features/meeting-notes/api-client'

const PER_PAGE = 8

/** How long the finished-row wash runs before the row settles into its normal state. */
const JUST_DONE_MS = 1200

type StatusFilter = AudioBatchDisplayStatus | 'all'
const FILTERS: ReadonlyArray<{ value: StatusFilter; key: string }> = [
  { value: 'all', key: 'transcriptions.list.filterAll' },
  { value: 'completed', key: 'transcriptions.list.filterCompleted' },
  { value: 'processing', key: 'transcriptions.list.filterProcessing' },
  { value: 'failed', key: 'transcriptions.list.filterFailed' },
]
const PILL_CLASS: Record<AudioBatchDisplayStatus, string> = {
  completed: 'is-done',
  processing: 'is-proc',
  failed: 'is-fail',
  cancelled: 'is-cancel',
}
/** Per-row wrapper modifier that drives the hover affordance (CSS). */
const WRAP_CLASS: Record<AudioBatchDisplayStatus, string> = {
  completed: 'is-completed',
  processing: 'is-processing',
  failed: 'is-failed',
  cancelled: '',
}

const rowKey = (row: { batchId: number; itemId: number }) => `${row.batchId}:${row.itemId}`

/** Answers whose first step is an upload; `dictation` goes to the desktop download instead. */
const UPLOAD_USE_CASES: ReadonlySet<UserUseCase> = new Set(['meeting', 'lecture', 'media'])
const DICTATION_DOWNLOAD_PATH = '/dictation#download'

interface TranscriptionsListPageProps {
  loadStoredToken?: () => string | null
  clearToken?: () => void
  fetchList?: typeof listAudioBatches
  retryItem?: typeof retryAudioItem
  renameItem?: typeof renameAudioItem
  /** Test seam — batch-status poll that drives the per-row progress bar. */
  fetchStatus?: typeof fetchAudioBatchStatus
  /** Test seam — completed-row transcript download. */
  downloadTranscript?: typeof fetchAudioItemTranscript
  /** Test seam — the upload modal's submit call. */
  submitBatch?: typeof submitAudioBatch
  /** Test seam — resolves the caller's per-file duration cap for the upload
   *  modal's client-side precheck (#173). */
  probeSession?: typeof probeInternalAccess
  /** Test seams for durable public-link management. */
  listOwnedShares?: typeof listOwnedPublicNoteShares
  revokeOwnedShare?: typeof revokeOwnedPublicNoteShare
  /** Test seam — removes an item from the current team workspace. */
  unshareItem?: typeof unshareAudioItemFromTeam
  /** Test seam — whether the「主要用途」question is still open for this user. */
  loadUseCaseQuestion?: typeof fetchUseCaseQuestionOpen
  /** Test seam — records the「主要用途」answer (`null` = skipped). */
  saveUseCaseAnswer?: typeof saveUseCase
  /** Test seam — client-side navigation; defaults to the App Router's `push`. */
  navigate?: (href: string) => void
}

type View =
  | { kind: 'loading' }
  | { kind: 'needsAuth' }
  | { kind: 'error' }
  | { kind: 'ready'; data: AudioTranscriptListResponse }

export function TranscriptionsListPage({
  loadStoredToken = readStoredToken,
  clearToken = clearStoredToken,
  fetchList = listAudioBatches,
  retryItem = retryAudioItem,
  renameItem = renameAudioItem,
  fetchStatus = fetchAudioBatchStatus,
  downloadTranscript = fetchAudioItemTranscript,
  submitBatch = submitAudioBatch,
  probeSession = probeInternalAccess,
  listOwnedShares = listOwnedPublicNoteShares,
  revokeOwnedShare = revokeOwnedPublicNoteShare,
  unshareItem = unshareAudioItemFromTeam,
  loadUseCaseQuestion = fetchUseCaseQuestionOpen,
  saveUseCaseAnswer = saveUseCase,
  navigate,
}: TranscriptionsListPageProps) {
  const { t } = useTranslation('web')
  const router = useRouter()
  const { open: openSignIn } = useAuthModal()
  const activeContext = useActiveContext()
  const hasTeamContext = /^org:[1-9]\d*$/.test(activeContext)
  const [view, setView] = useState<View>({ kind: 'loading' })
  // The caller's per-file duration cap (minutes), resolved once for the upload
  // modal's client-side precheck. null = uncapped / unknown (never blocks).
  const [maxFileMinutes, setMaxFileMinutes] = useState<number | null>(null)
  const [maxFileBytes, setMaxFileBytes] = useState<number | null>(null)
  const [currentPlan, setCurrentPlan] = useState<string | null>(null)
  const [taigiEnabled, setTaigiEnabled] = useState(false)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [page, setPage] = useState(1)
  const [scope, setScope] = useState<'personal' | 'team'>('personal')
  const [reloadNonce, setReloadNonce] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)
  const [ownedSharesOpen, setOwnedSharesOpen] = useState(false)
  // The「主要用途」question: open only once the server says this user has not
  // answered it. Starts closed so an old API, a failed probe or a slow one never
  // flashes the question over the empty state.
  const [useCaseOpen, setUseCaseOpen] = useState(false)
  // Rows that just flipped from processing to finished, so the completion wash
  // plays once. Keyed `${batchId}:${itemId}`.
  const [justDone, setJustDone] = useState<ReadonlySet<string>>(() => new Set())
  // Which completed row's download menu is open (`${batchId}:${itemId}`), and the
  // set of in-flight `${batchId}:${itemId}:${format}` download keys.
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null)
  const [downloadingKeys, setDownloadingKeys] = useState<ReadonlySet<string>>(() => new Set())
  const uploadBtnRef = useRef<HTMLButtonElement | null>(null)
  const stateRef = useRef({ q, status, page, scope })
  stateRef.current = { q, status, page, scope }
  // Monotonic request id: drop a response that's been superseded by a newer
  // search/filter/page change (out-of-order resolution would show stale rows).
  const loadSeqRef = useRef(0)
  const capSeqRef = useRef(0)
  const useCaseSeqRef = useRef(0)
  const filterEffectMountedRef = useRef(false)

  const load = useCallback(async () => {
    const token = loadStoredToken()
    if (!token) {
      setView({ kind: 'needsAuth' })
      return
    }
    const seq = ++loadSeqRef.current
    const { q: cq, status: cs, page: cp, scope: currentScope } = stateRef.current
    // Up to two attempts: a recoverable stale/invalid workspace context (403
    // forbidden / 400 invalid) is self-healed by the fetch (api-client clears the
    // dead header), so a second attempt re-resolves the smart default and
    // succeeds. A recoverable context must NEVER clearToken — that logs the user
    // out of a perfectly valid session (the exact wedge a stale context caused).
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const data = await fetchList({
          page: cp,
          perPage: PER_PAGE,
          q: cq,
          status: cs,
          scope: currentScope,
        })
        if (seq !== loadSeqRef.current) return
        setView({ kind: 'ready', data })
        return
      } catch (cause) {
        reportHandledException(cause, {
          source: 'app/app/transcriptions/transcriptions-list-page.tsx',
        })
        if (seq !== loadSeqRef.current) return
        if (attempt === 0 && isRecoverableActiveContextError(cause)) continue
        // A 401 (token dead) or a non-recoverable 403 signs the user out. The
        // recoverable context 403 (active_context_forbidden) was already handled
        // and retried above, so any 403 reaching here is a genuine account-level
        // block — on these per-user endpoints that is a deactivated account
        // (auth_middleware deletes the token + returns 403), which must logout.
        // A disabled team seat keeps the session: the workspace is still the
        // member's, only its content is closed.
        if (
          cause instanceof AudioBatchApiError &&
          (cause.status === 401 || cause.status === 403) &&
          !isTeamMemberDisabledError(cause)
        ) {
          clearToken()
          setView({ kind: 'needsAuth' })
          return
        }
        console.error('[transcriptions] list load failed', cause)
        setView({ kind: 'error' })
        return
      }
    }
  }, [loadStoredToken, clearToken, fetchList])

  // Resolve the caller's per-file duration cap for the upload modal's precheck.
  // Best-effort and decoupled from `load`: a probe failure leaves the cap null
  // (the upload modal then skips the precheck and relies on the server cap), so
  // it never blocks the page or an upload.
  const resolveCap = useCallback(async () => {
    const seq = ++capSeqRef.current
    const token = loadStoredToken()
    if (!token) {
      if (seq !== capSeqRef.current) return
      setMaxFileMinutes(null)
      setMaxFileBytes(null)
      setCurrentPlan(null)
      setTaigiEnabled(false)
      return
    }
    // Retry once on a recoverable stale-context error: probeInternalAccess has
    // already cleared the bad X-Active-Context, so the second attempt succeeds.
    // Mirrors the list `load` path — otherwise a self-healing context would
    // silently disable the precheck for the rest of the page.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const probe = await probeSession()
        if (seq !== capSeqRef.current) return
        setMaxFileMinutes(probe.maxFileMinutes)
        setMaxFileBytes(probe.maxFileBytes)
        setCurrentPlan(probe.plan)
        setTaigiEnabled(probe.taigiEnabled)
        return
      } catch (cause) {
        reportHandledException(cause, {
          source: 'app/app/transcriptions/transcriptions-list-page.tsx',
        })
        if (seq !== capSeqRef.current) return
        if (attempt === 0 && isRecoverableActiveContextError(cause)) continue
        setMaxFileMinutes(null)
        setMaxFileBytes(null)
        setCurrentPlan(null)
        setTaigiEnabled(false)
        return
      }
    }
  }, [loadStoredToken, probeSession])

  // Best-effort like the cap probe: any failure leaves the question closed and
  // the page on its ordinary empty state.
  const resolveUseCaseQuestion = useCallback(async () => {
    const seq = ++useCaseSeqRef.current
    if (!loadStoredToken()) {
      setUseCaseOpen(false)
      return
    }
    try {
      const open = await loadUseCaseQuestion()
      if (seq !== useCaseSeqRef.current) return
      setUseCaseOpen(open)
    } catch (cause) {
      reportHandledException(cause, {
        source: 'app/app/transcriptions/transcriptions-list-page.tsx',
      })
      if (seq !== useCaseSeqRef.current) return
      setUseCaseOpen(false)
    }
  }, [loadStoredToken, loadUseCaseQuestion])

  const clearWorkspace = useCallback(() => {
    loadSeqRef.current += 1
    capSeqRef.current += 1
    setView({ kind: 'loading' })
    setMaxFileMinutes(null)
    setMaxFileBytes(null)
    setCurrentPlan(null)
    setTaigiEnabled(false)
    // Progress clears itself: the polling hook keys off the visible processing
    // rows, and the view reset below empties them.
    setJustDone(new Set())
    setScope('personal')
    setPage(1)
    stateRef.current = { ...stateRef.current, page: 1, scope: 'personal' }
    setModalOpen(false)
    setOwnedSharesOpen(false)
    setOpenMenuKey(null)
    setDownloadingKeys(new Set())
  }, [])

  useWorkspaceScopedLoader({
    clear: clearWorkspace,
    load: async () => {
      await Promise.all([load(), resolveCap(), resolveUseCaseQuestion()])
    },
  })

  useEffect(() => {
    if (!hasTeamContext && scope === 'team') {
      setScope('personal')
      setPage(1)
    }
  }, [hasTeamContext, scope])

  // Debounce the filename search; status/page/reload changes apply immediately.
  useEffect(() => {
    if (!filterEffectMountedRef.current) {
      filterEffectMountedRef.current = true
      return
    }
    const handle = setTimeout(() => void load(), q ? 250 : 0)
    return () => clearTimeout(handle)
  }, [q, status, page, scope, reloadNonce, load])

  useEffect(
    () =>
      onAuthTokenChange(() => {
        void load()
        void resolveCap()
        void resolveUseCaseQuestion()
      }),
    [load, resolveCap, resolveUseCaseQuestion]
  )

  // ── Per-row progress ─────────────────────────────────────────────────────
  // Poll the batch status of every row shown as processing and turn it into the
  // stage view the row renders, reconciling the list once a tracked item settles
  // (so the row flips to completed/failed with its real meta + actions).
  const processingRows =
    view.kind === 'ready' ? view.data.items.filter((i) => i.status === 'processing') : []
  const batchIdsKey = Array.from(new Set(processingRows.map((row) => row.batchId)))
    .sort((a, b) => a - b)
    .join(',')

  const progress = useBatchProgressPolling({
    batchIdsKey,
    fetchStatus,
    onSettled: () => setReloadNonce((n) => n + 1),
    // A genuine auth failure (dead/revoked token) must NOT spin forever on a
    // stale credential: bump a reload so the `load` path surfaces sign-in. A
    // recoverable stale context self-heals (the api-client already cleared it,
    // so the next tick re-resolves) and transient blips just retry.
    isFatalError: (error) =>
      error instanceof AudioBatchApiError &&
      (error.status === 401 || error.status === 403) &&
      !isRecoverableActiveContextError(error),
    onAuthFailure: () => {
      console.warn(
        '[transcriptions] progress poll hit an auth failure — stopping; reloading to surface sign-in'
      )
      setReloadNonce((n) => n + 1)
    },
  })

  // The completion wash: play it once, for rows the user actually watched
  // finish. A row that was already complete when the page loaded gets nothing —
  // washing it would suggest something just happened.
  const watchedRef = useRef<Set<string>>(new Set())
  const processingKeys = processingRows.map(rowKey).join(',')
  useEffect(() => {
    for (const key of processingKeys ? processingKeys.split(',') : []) watchedRef.current.add(key)
  }, [processingKeys])

  const readyKeys =
    view.kind === 'ready'
      ? view.data.items
          .filter((i) => i.status === 'completed')
          .map(rowKey)
          .join(',')
      : ''
  useEffect(() => {
    const finished = (readyKeys ? readyKeys.split(',') : []).filter((key) =>
      watchedRef.current.has(key)
    )
    if (finished.length === 0) return
    for (const key of finished) watchedRef.current.delete(key)
    setJustDone((prev) => new Set([...prev, ...finished]))
    const timer = setTimeout(() => {
      setJustDone((prev) => {
        const next = new Set(prev)
        for (const key of finished) next.delete(key)
        return next
      })
    }, JUST_DONE_MS)
    return () => clearTimeout(timer)
  }, [readyKeys])

  // Close the open download menu on any outside click or Escape.
  useEffect(() => {
    if (!openMenuKey) return
    const close = () => setOpenMenuKey(null)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenuKey(null)
    }
    document.addEventListener('click', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [openMenuKey])

  const clearFilters = () => {
    setQ('')
    setStatus('all')
    setPage(1)
  }

  const closeModal = useCallback(() => {
    setModalOpen(false)
    // Return focus to the opener (a11y) — the header button is always mounted.
    uploadBtnRef.current?.focus()
  }, [])

  const handleSubmitted = useCallback(
    (response: AudioBatchSubmitResponse) => {
      closeModal()
      toast.success(t('transcriptions.upload.submitted', { count: response.items.length }))
      // New files are the newest uploads → reset to the unfiltered first page so
      // they show at the top as processing, then reload to pull the server truth.
      setQ('')
      setStatus('all')
      setPage(1)
      setReloadNonce((n) => n + 1)
    },
    [closeModal, t]
  )

  const handleRetry = useCallback(
    async (row: AudioTranscriptListItem) => {
      const token = loadStoredToken()
      if (!token) return
      try {
        await retryItem(row.batchId, row.itemId)
        toast.success(t('transcriptions.list.retryQueued'))
        // Optimistically flip the row to processing.
        setView((prev) =>
          prev.kind === 'ready'
            ? {
                kind: 'ready',
                data: {
                  ...prev.data,
                  items: prev.data.items.map((i) =>
                    i.itemId === row.itemId && i.batchId === row.batchId
                      ? { ...i, status: 'processing' }
                      : i
                  ),
                },
              }
            : prev
        )
      } catch (cause) {
        reportHandledException(cause, {
          source: 'app/app/transcriptions/transcriptions-list-page.tsx',
        })
        if (cause instanceof AudioBatchApiError && cause.code === 'insufficient_credits') {
          toast.error(
            toastTitle(t('transcriptions.list.retryInsufficientCredits', { balance: cause.balance }))
          )
          return
        }
        if (cause instanceof AudioBatchApiError && cause.code === 'file_too_long_for_plan') {
          toast.error(toastTitle(t('transcriptions.list.retryFileTooLong')))
          return
        }
        if (cause instanceof AudioBatchApiError && cause.code === 'audio_unavailable') {
          toast.error(toastTitle(t('transcriptions.list.retryNeedsReupload')))
          return
        }
        console.error('[transcriptions] retry failed', cause)
        toast.error(toastTitle(t('transcriptions.list.retryFailed')))
      }
    },
    [loadStoredToken, retryItem, t]
  )

  const handleRename = useCallback(
    async (row: AudioTranscriptListItem, next: string) => {
      const token = loadStoredToken()
      // Surface (don't swallow) a missing token: throw so the rename hook reverts
      // the title and toasts the failure (it closes the editor either way), instead
      // of silently committing a no-op.
      if (!token) throw new Error('Not authenticated')
      // Pessimistic: persist first, then patch the row title with the server's
      // (extension-free) value. The hook toasts if this throws, so no revert.
      const updated = await renameItem(row.batchId, row.itemId, next)
      setView((prev) =>
        prev.kind === 'ready'
          ? {
              kind: 'ready',
              data: {
                ...prev.data,
                items: prev.data.items.map((i) =>
                  i.itemId === row.itemId && i.batchId === row.batchId
                    ? { ...i, title: updated.filename }
                    : i
                ),
              },
            }
          : prev
      )
    },
    [loadStoredToken, renameItem]
  )

  const handleDownload = useCallback(
    async (row: AudioTranscriptListItem, format: AudioBatchDownloadFormat) => {
      const token = loadStoredToken()
      if (!token) return
      const key = `${rowKey(row)}:${format}`
      if (downloadingKeys.has(key)) return
      setDownloadingKeys((prev) => new Set(prev).add(key))
      try {
        const body = await downloadTranscript(row.batchId, row.itemId, format, {})
        downloadTextFile(
          body,
          deriveTranscriptFilename(row.title, row.itemId, format),
          TRANSCRIPT_FORMAT_MIME[format]
        )
        setOpenMenuKey(null)
      } catch (cause) {
        reportHandledException(cause, {
          source: 'app/app/transcriptions/transcriptions-list-page.tsx',
        })
        console.error('[transcriptions] download failed', cause)
        toast.error(t('transcriptions.export.downloadFailed'))
      } finally {
        setDownloadingKeys((prev) => {
          if (!prev.has(key)) return prev
          const nextSet = new Set(prev)
          nextSet.delete(key)
          return nextSet
        })
      }
    },
    [loadStoredToken, downloadingKeys, downloadTranscript, t]
  )

  const handleUnshare = useCallback(
    async (row: AudioTranscriptListItem) => {
      try {
        await unshareItem(row.batchId, row.itemId)
        setView((prev) =>
          prev.kind === 'ready'
            ? {
                kind: 'ready',
                data: {
                  ...prev.data,
                  total: Math.max(0, prev.data.total - 1),
                  items: prev.data.items.filter(
                    (item) => item.batchId !== row.batchId || item.itemId !== row.itemId
                  ),
                },
              }
            : prev
        )
        toast.success(t('transcriptions.team.removed'))
      } catch (cause) {
        reportHandledException(cause, {
          source: 'app/app/transcriptions/transcriptions-list-page.tsx',
        })
        console.error('[transcriptions] unshare failed', cause)
        toast.error(t('transcriptions.team.removeFailed'))
      }
    },
    [t, unshareItem]
  )

  // The question closes the moment it is answered and the first step runs
  // straight away; saving happens alongside. A failed save is reported, not
  // surfaced: the answer only personalises later email, so it must not stand
  // between the user and the step they just chose (the question simply comes
  // back on the next visit).
  const handleUseCaseAnswer = (useCase: UserUseCase | null) => {
    setUseCaseOpen(false)
    if (useCase === null) trackEvent(ANALYTICS_EVENTS.useCaseQuestionDismissed)
    else trackEvent(ANALYTICS_EVENTS.useCaseSelected, { use_case: useCase })
    saveUseCaseAnswer(useCase).catch((cause: unknown) => {
      reportHandledException(cause, {
        source: 'app/app/transcriptions/transcriptions-list-page.tsx',
      })
      console.error('[transcriptions] use-case answer failed to save', cause)
    })
    if (useCase !== null && UPLOAD_USE_CASES.has(useCase)) setModalOpen(true)
    // Client-side navigation keeps this page's JS alive, so the save above is
    // not cancelled the way a full page load would cancel it.
    else if (useCase === 'dictation') (navigate ?? router.push)(DICTATION_DOWNLOAD_PATH)
  }

  if (view.kind === 'loading') {
    return <div className="main center-note">{t('transcriptions.loading')}</div>
  }
  if (view.kind === 'needsAuth') {
    return (
      <div className="main center-note">
        <h1>{t('transcriptions.needAuthHeading')}</h1>
        <p className="lead">{t('transcriptions.needAuthBody')}</p>
        <button
          className="btn btn-primary btn-sm"
          type="button"
          onClick={() => openSignIn({ redirectTo: null })}
        >
          {t('transcriptions.signInCta')}
        </button>
      </div>
    )
  }
  if (view.kind === 'error') {
    return (
      <div className="main center-note">
        <p>{t('transcriptions.errorLoad')}</p>
        <button className="btn btn-secondary btn-sm" type="button" onClick={() => void load()}>
          {t('transcriptions.tryAgain')}
        </button>
      </div>
    )
  }

  const { data } = view
  const totalPages = Math.max(1, Math.ceil(data.total / data.perPage))
  const filtersActive = q.trim() !== '' || status !== 'all'
  // Only a genuinely empty personal list: a search/filter or the team tab can be
  // empty for someone who already has transcripts.
  const askUseCase =
    useCaseOpen && data.items.length === 0 && !filtersActive && scope === 'personal'

  return (
    <div className="main">
      <WorkspacePageHeader
        title={t('transcriptions.list.heading')}
        subtitle={t('transcriptions.list.subhead')}
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              onClick={() => setOwnedSharesOpen(true)}
            >
              {t('share.managerOpenButton')}
            </button>
            <button
              ref={uploadBtnRef}
              className="btn btn-primary btn-sm"
              type="button"
              onClick={() => setModalOpen(true)}
            >
              {t('transcriptions.list.uploadCta')}
            </button>
          </div>
        }
      />

      {hasTeamContext ? (
        <div className="scope-tabs" role="tablist" aria-label={t('transcriptions.team.scopeLabel')}>
          {/* value-list: local — 轉錄清單「我的／團隊共享」分頁（列表 query 的 scope），不是 organizations.kind */}
          {(['personal', 'team'] as const).map((value) => (
            <button
              key={value}
              id={`transcription-scope-${value}`}
              type="button"
              role="tab"
              aria-selected={scope === value}
              aria-controls="transcription-list-panel"
              onClick={() => {
                setScope(value)
                setPage(1)
              }}
            >
              {t(`transcriptions.team.${value}`)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="toolbar">
        <label className="search">
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5L14 14" />
          </svg>
          <input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setPage(1)
            }}
            placeholder={t('transcriptions.list.searchPlaceholder')}
            aria-label={t('transcriptions.list.searchAria')}
          />
        </label>
        <SegmentedControl
          aria-label={t('transcriptions.list.filterGroupAria')}
          variant="unstyled"
          className="filter"
          value={status}
          options={FILTERS.map((filter) => ({ value: filter.value, label: t(filter.key) }))}
          onValueChange={(value) => {
            setStatus(value)
            setPage(1)
          }}
        />
        <span className="count">{t('transcriptions.list.count', { count: data.total })}</span>
      </div>

      <div
        id="transcription-list-panel"
        role={hasTeamContext ? 'tabpanel' : undefined}
        aria-labelledby={hasTeamContext ? `transcription-scope-${scope}` : undefined}
      >
        {askUseCase ? (
          <UseCaseQuestion onAnswer={handleUseCaseAnswer} />
        ) : data.items.length === 0 ? (
          <div className="empty">
            <p>
              {filtersActive
                ? t('transcriptions.list.emptyNoResults')
                : t('transcriptions.list.empty')}
            </p>
            {filtersActive ? (
              <button className="btn btn-secondary btn-sm" type="button" onClick={clearFilters}>
                {t('transcriptions.list.clearFilter')}
              </button>
            ) : (
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                onClick={() => setModalOpen(true)}
              >
                {t('transcriptions.list.emptyCta')}
              </button>
            )}
          </div>
        ) : (
          <div className="tlist">
            {data.items.map((row) => (
              <ListRow
                key={rowKey(row)}
                row={row}
                progress={progress[progressRowKey(row.batchId, row.itemId)] ?? null}
                justDone={justDone.has(rowKey(row))}
                menuOpen={openMenuKey === rowKey(row)}
                downloading={[...downloadingKeys].some((k) => k.startsWith(`${rowKey(row)}:`))}
                onToggleMenu={() =>
                  setOpenMenuKey((prev) => (prev === rowKey(row) ? null : rowKey(row)))
                }
                onRetry={handleRetry}
                onRename={handleRename}
                onDownload={handleDownload}
                teamScope={scope === 'team'}
                onUnshare={handleUnshare}
              />
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 ? (
        <nav className="pager" aria-label={t('transcriptions.list.paginationAria')}>
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('transcriptions.list.prevPage')}
          </button>
          <span className="pg-ind">
            {t('transcriptions.list.pageIndicator', { page, pages: totalPages })}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            {t('transcriptions.list.nextPage')}
          </button>
        </nav>
      ) : null}

      <UploadModal
        open={modalOpen}
        onClose={closeModal}
        onSubmitted={handleSubmitted}
        loadStoredToken={loadStoredToken}
        submit={submitBatch}
        maxFileMinutes={maxFileMinutes}
        maxFileBytes={maxFileBytes}
        currentPlan={currentPlan}
        taigiEnabled={taigiEnabled}
        teamContext={hasTeamContext}
      />
      {ownedSharesOpen ? (
        <OwnedPublicNoteSharesDialog
          open
          onClose={() => setOwnedSharesOpen(false)}
          listSharesFn={listOwnedShares}
          revokeShareFn={revokeOwnedShare}
        />
      ) : null}
    </div>
  )
}

function ListRow({
  row,
  progress,
  justDone,
  menuOpen,
  downloading,
  onToggleMenu,
  onRetry,
  onRename,
  onDownload,
  teamScope,
  onUnshare,
}: {
  row: AudioTranscriptListItem
  /** Live stage view while this row is processing; null until the first poll lands. */
  progress: ItemStageView | null
  justDone: boolean
  menuOpen: boolean
  downloading: boolean
  onToggleMenu: () => void
  onRetry: (row: AudioTranscriptListItem) => void
  onRename: (row: AudioTranscriptListItem, next: string) => Promise<void>
  onDownload: (row: AudioTranscriptListItem, format: AudioBatchDownloadFormat) => void
  teamScope: boolean
  onUnshare: (row: AudioTranscriptListItem) => void
}) {
  const { t } = useTranslation('web')
  const title = stripAudioExtension(row.title ?? t('transcriptions.list.untitled'))
  const date = formatDate(row.createdAt)
  // A legacy personal-list payload may not carry capabilities yet, so keep its
  // owner retry available. Team scope is new and always permission-aware: fail
  // closed there so a missing field never lets a collaborator spend the
  // uploader's original credit pool by retrying their failed item.
  const canRetry = row.permissions?.canDelete ?? !teamScope
  const [editing, setEditing] = useState(false)
  const pencilRef = useRef<HTMLButtonElement>(null)
  const restoreFocus = useRef(false)
  const editable = useInlineEditable<HTMLDivElement>(
    title,
    (next) => onRename(row, next),
    () => toast.error(t('transcriptions.actions.saveFailed')),
    {
      onClose: () => {
        restoreFocus.current = true
        setEditing(false)
      },
    }
  )
  // Keyed on `editing` so it fires once on each transition (not on every
  // re-render, which would yank the caret to the end mid-typing):
  // - opening → drop the caret into the title (caret at end);
  // - closing → return focus to the pencil so keyboard users keep their place
  //   (guarded by `restoreFocus` so it never fires on the initial mount).
  useEffect(() => {
    if (editing) {
      editable.focusEnd()
    } else if (restoreFocus.current) {
      restoreFocus.current = false
      pencilRef.current?.focus()
    }
  }, [editing])
  // Per-row deletion countdown: only completed rows have a clock, and only when
  // within the warn window. ≤ SOON days switches to the error tone.
  const daysLeft = daysUntilExpiry(row.expiresAt)
  const showExpiry =
    row.status === 'completed' && daysLeft !== null && daysLeft <= RETENTION_WARN_DAYS
  const expirySoon = showExpiry && (daysLeft as number) <= RETENTION_SOON_DAYS
  // On a paid plan only the audio expires, so the countdown must say so — an
  // unqualified "deleted in N days" would be a false alarm about the transcript.
  //
  // A retained row also STAYS past its date forever (that is the steady state,
  // not an edge case), so the past-date case needs its own copy: clamping a
  // negative `daysLeft` into the "today" branch would leave every retained
  // transcript older than the window permanently claiming a deletion that
  // already happened.
  const audioOnlyExpiry = row.retentionPolicy === 'retain_transcript'

  if (editing) {
    // Edit the title in place (no input box) — same contentEditable affordance
    // as the detail header. Enter/blur commits, Escape cancels.
    return (
      <div className={`trow-wrap${WRAP_CLASS[row.status] ? ` ${WRAP_CLASS[row.status]}` : ''}`}>
        <div className="trow trow-edit">
          <div
            ref={editable.ref}
            className="tr-title"
            data-testid="rename-row"
            aria-label={t('transcriptions.rename.ariaLabel')}
            {...editable.elementProps}
          />
        </div>
      </div>
    )
  }

  // A row that is being worked on, or that stopped, shows its stage instead of
  // its duration/status pill. `stageView` is null for every other row.
  const stageView: ItemStageView | null =
    row.status === 'processing'
      ? (progress ?? pendingStageView())
      : row.status === 'failed'
        ? failedStageView(row.progress)
        : null

  const wrapClass = `trow-wrap${WRAP_CLASS[row.status] ? ` ${WRAP_CLASS[row.status]}` : ''}${
    menuOpen ? ' dl-open' : ''
  }${justDone ? ' just-done' : ''}`

  return (
    <div className={wrapClass}>
      <Link
        className="trow"
        href={`/app/transcriptions/${row.batchId}?i=${row.itemId}`}
        aria-label={t('transcriptions.list.openAria', { title })}
      >
        <div className="tr-main">
          <div className="tr-title">{title}</div>
          {stageView ? (
            <RowStageText
              errorCode={row.errorCode}
              view={stageView}
              trailing={
                teamScope && row.uploader?.email ? (
                  <span className="sub">
                    {' · '}
                    {t('transcriptions.team.uploadedBy', { email: row.uploader.email })}
                  </span>
                ) : null
              }
            />
          ) : (
          <div className="tr-meta">
            <span>
              {row.status === 'cancelled' ? (
                t('transcriptions.list.metaCancelled')
              ) : (
                <>
                  {formatTimecode(row.durationSecs ?? 0)}
                  {showExpiry ? (
                    <span className={`expiry${expirySoon ? ' soon' : ''}`}>
                      {' · '}
                      {audioOnlyExpiry
                        ? (daysLeft as number) < 0
                          ? t('transcriptions.list.audioExpired')
                          : (daysLeft as number) === 0
                            ? t('transcriptions.list.audioExpiresToday')
                            : t('transcriptions.list.audioExpiresInDays', {
                                count: daysLeft as number,
                              })
                        : (daysLeft as number) <= 0
                          ? t('transcriptions.list.expiresToday')
                          : t('transcriptions.list.expiresInDays', { count: daysLeft as number })}
                    </span>
                  ) : null}
                </>
              )}
            </span>
            {teamScope && row.uploader?.email ? (
              <span> · {t('transcriptions.team.uploadedBy', { email: row.uploader.email })}</span>
            ) : null}
          </div>
          )}
        </div>
        {/* The status pill's cell carries the global percentage while an item is
            being worked on. Same cell, same row height — the list must not jump
            when a file finishes. */}
        {stageView ? (
          <RowStagePercent view={stageView} />
        ) : (
          <span className={`pill ${PILL_CLASS[row.status]}`}>
            <span className="pdot" />
            {t(`transcriptions.batchStatus.${row.status}`)}
          </span>
        )}
        <span className="tr-date">{date}</span>
      </Link>

      {/* Hover action cluster at the date area: a rename pencil (every status but
          failed — a failed file is renamed from its detail page) alongside the
          status action (completed → download menu, failed → retry). */}
      <div className="row-actions">
        {row.status !== 'failed' && row.permissions?.canEditContent !== false ? (
          <button
            ref={pencilRef}
            type="button"
            className="rename-btn"
            aria-label={t('transcriptions.rename.openRowAria', { title })}
            title={t('transcriptions.rename.cta')}
            onClick={() => setEditing(true)}
          >
            <Pencil aria-hidden="true" />
          </button>
        ) : null}

        {teamScope && row.permissions?.canUnshare ? (
          <button
            type="button"
            className="unshare-btn"
            aria-label={t('transcriptions.team.removeAria', { title })}
            onClick={() => onUnshare(row)}
          >
            {t('transcriptions.team.remove')}
          </button>
        ) : null}

        {row.status === 'completed' ? (
          <div className="dl-wrap">
            <button
              type="button"
              className="dl-btn"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={t('transcriptions.list.downloadAria', { title })}
              onClick={(e) => {
                e.stopPropagation()
                onToggleMenu()
              }}
            >
              {t('transcriptions.export.downloadLabel')}
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4 6l4 4 4-4" />
              </svg>
            </button>
            {menuOpen ? (
              <div
                className="dl-menu"
                role="menu"
                aria-label={t('transcriptions.list.downloadMenuAria')}
              >
                {AUDIO_BATCH_DOWNLOAD_FORMATS.map((fmt) => (
                  <button
                    key={fmt.value}
                    type="button"
                    role="menuitem"
                    disabled={downloading}
                    aria-label={t('transcriptions.list.downloadFmtAria', {
                      title,
                      format: fmt.label,
                    })}
                    onClick={(e) => {
                      e.stopPropagation()
                      onDownload(row, fmt.value)
                    }}
                  >
                    {fmt.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {row.status === 'failed' && canRetry ? (
          <button
            type="button"
            className="retry-btn"
            aria-label={t('transcriptions.list.retryAria', { title })}
            onClick={() => onRetry(row)}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M13.6 8a5.6 5.6 0 1 1-1.7-4" />
              <path d="M12.5 1.8V4.3H10" />
            </svg>
            {t('transcriptions.list.retry')}
          </button>
        ) : null}
      </div>

      {/* The row's own bottom hairline, filling with ink. Not an added bar: a
          processing row and a finished row are the same height. */}
      {stageView ? <RowProgressRail view={stageView} /> : null}
    </div>
  )
}
